// Lightweight query-timing instrumentation. Wraps any async query and
// logs to console with a [perf] prefix (debug for fast, warn for slow).
//
// SLOW_THRESHOLD_MS: 1500ms — calibrated against the UX target. Anything
// above ~1s feels broken when the table blanks; below that the stale-
// while-revalidate overlay hides the wait.
//
// Persistence: phase-1 is console-only. Phase-2 lands a dedicated
// `query_perf` table with a TTL so we can analyse trends without
// polluting the events audit trail.
//
// Usage:
//   const rows = await trackQuery('useAllDocuments.fetchRows', () =>
//     supabase.from('documents').select(...).range(0, 99)
//   )

export const SLOW_THRESHOLD_MS = 1500

type PerfMeta = Record<string, string | number | boolean | null | undefined>

export async function trackQuery<T>(
  name: string,
  run: () => Promise<T>,
  meta: PerfMeta = {},
): Promise<T> {
  const start = performance.now()
  try {
    const result = await run()
    const ms = Math.round(performance.now() - start)
    logTiming(name, ms, meta, /* ok */ true)
    return result
  } catch (err) {
    const ms = Math.round(performance.now() - start)
    logTiming(name, ms, meta, /* ok */ false, err)
    throw err
  }
}

function logTiming(
  name: string,
  ms: number,
  meta: PerfMeta,
  ok: boolean,
  err?: unknown,
): void {
  const tag = ok ? '[perf]' : '[perf:err]'
  const slow = ms >= SLOW_THRESHOLD_MS
  // console.info is visible at DevTools' default level; debug is hidden
  // unless "Verbose" is on. We want timings visible by default so the team
  // can spot regressions without changing log filter.
  const fn = !ok ? console.warn : slow ? console.warn : console.info
  if (!ok) {
    fn(`${tag} ${name} ${ms}ms`, { ...meta, error: err instanceof Error ? err.message : String(err) })
  } else {
    fn(`${tag} ${name} ${ms}ms`, meta)
  }
}
